import { Suspense, useCallback, useMemo, useState } from "react";
import * as React from "react";

import { DialogLoadErrorBoundary } from "components/dialog-load-error-boundary/dialog-load-error-boundary.component";
import DialogLoadingFallback from "components/dialog-loading-fallback";
import { useStickyTrue } from "hooks/use-sticky-true.hook";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import { UPLOAD_SOURCE } from "../components/fax-form/fax-form.constants";
import { Patient } from "../services/types/patients.type";

type FaxProviderProps = {
  children?: React.ReactNode;
};

const FaxForm = lazyWithRetry(
  () => import("../components/fax-form/fax-form.component"),
  "fax-form"
);

const FaxContext = React.createContext<any>(null);

export function FaxProvider({ children }: FaxProviderProps) {
  const [openModal, setOpenModal] = useState(false);
  const shouldMountFaxForm = useStickyTrue(openModal);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [initialDocuments, setInitialDocuments] = useState<File[]>([]);
  const [source, setSource] = useState<string>(UPLOAD_SOURCE);

  const handleOpen = useCallback((patient?: Patient) => {
    setPatient(patient || null);
    setOpenModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setPatient(null);
    setInitialDocuments([]);
  }, []);

  const value = useMemo(
    () => ({
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setSource,
      setInitialDocuments
    }),
    [
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setSource,
      setInitialDocuments
    ]
  );

  return (
    <FaxContext.Provider value={value}>
      {children}
      {shouldMountFaxForm && (
        <DialogLoadErrorBoundary dialogName="fax-form">
          <Suspense fallback={<DialogLoadingFallback />}>
            <FaxForm
              isOpen={openModal}
              onClose={handleClose}
              patient={patient || undefined}
              selectedFiles={selectedFiles}
              initialDocuments={initialDocuments}
              source={source}
            />
          </Suspense>
        </DialogLoadErrorBoundary>
      )}
    </FaxContext.Provider>
  );
}

export default function useFax() {
  const faxContext = React.useContext(FaxContext);
  if (!faxContext) {
    throw new Error(`useFax must be used within a FaxProvider`);
  }
  const {
    handleOpen,
    selectedFiles,
    setSelectedFiles,
    setSource,
    setInitialDocuments
  } = faxContext;
  return {
    handleOpen,
    selectedFiles,
    setSelectedFiles,
    setSource,
    setInitialDocuments
  };
}
